// @ts-strict-ignore
import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  SohoAlertDirective,
  SohoBusyIndicatorDirective,
  SohoMessageService,
  SohoSpinboxComponent
} from 'ids-enterprise-ng';
import { Subscription } from 'rxjs';

import { LMPrintCsvInfo, LMPrintDialog, LMPanelDetail } from '@modal/print-dialog/lm-print-dialog.service';
import { LMPrintRequestService } from '@modal/print-dialog/lm-print-request.service';

import { AppConstants } from '@utils/app.constants';
import { LMLookupBasicComponent } from '@field/lookup';
import { LMFieldValueChangedEvent, LMFormRequestService } from '@app/components';
import { LMFormatUtilService, LMUrlUtilService } from '@app/services';
import { LMListDropdownComponent } from '@components/lmlistdropdown';
import { LMGlobalPersonalizationService } from '@app/states';
import {ListResource} from "@app/resources";
import { first } from 'rxjs/operators';

const EXCEL_ROW_LIMIT = 1048574;

@Component({
  selector: 'lm-print-csv-dialog',
  templateUrl: './lm-print-csv-dialog.component.html',
  styleUrls: [ './lm-print-csv-dialog.component.css' ]
})
export class LMPrintCsvDialogComponent implements LMPrintDialog, AfterViewInit, OnDestroy, OnInit {
  constructor(
    private sohoMessageService: SohoMessageService,
    private formBuilder: FormBuilder,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmPrintRequestService: LMPrintRequestService,
    private lmFromRequestService: LMFormRequestService,
    private lmFormatUtilService: LMFormatUtilService) {}

  @ViewChild('modalPlaceholder', { read: ViewContainerRef, static: true }) modalPlaceholder: ViewContainerRef;
  @ViewChild('maxLevels') maxLevels: SohoSpinboxComponent;
  @ViewChild('maxRows') maxRows: SohoSpinboxComponent;
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) busyIndicator: SohoBusyIndicatorDirective;
  @ViewChildren(SohoAlertDirective) alerts: QueryList<SohoAlertDirective>;
  @ViewChild(LMLookupBasicComponent) lmLookupBasicCsv: LMLookupBasicComponent;
  @ViewChild('dynamicActorsComponent') dynamicActorsComponent: LMListDropdownComponent;

  @Input() printInfo: LMPrintCsvInfo;
  private subscriptions: Subscription[] = [];

  dynamicActorOptions: LMListDropdownOptions = {
    businessClass: 'Actor',
    maxValues: 20,
    searchFields: [ 'Actor', 'PersonName.GivenName',  'PersonName.FamilyName', 'PersonName.PreferredGivenName' ],
    selectList: 'AdHocDistributionGroupActorList' };
  showDynamicActors = false;
  atLeastOneDynamicActor = false;
  dynamicActorsLabel = Soho.Locale.translate('PDFAdHocActors');

  reportDistGroupLabel = '';
  sourceFieldName = '';

  includeExportOption = false;
  includeDetailOption = false;
  csvType: string;
  detailList: LMPanelDetail[] = [];
  excelRowLimit: number;

  readonly formatOptions: LMPrintOption[] = [
    { label: 'CSV', value: 1 },
    { label: 'Excel', value: 2 },
  ];

  readonly columnHeaderOptions: LMPrintOption[] = [
    { label: 'FieldID', value: 0 },
    { label: 'FieldLabel', value: 1 },
  ];

  public exportOptions: LMPrintOption[] = [];
  public detailsOptions: LMPrintOption[] = [];

  printForm: FormGroup;
  allRecordsAlert: SohoAlertDirective;
  dynamicActorsAlert: SohoAlertDirective;
  multiThreadAlert: SohoAlertDirective;
  useCompactFormLayout = false;
  useTabs = true;
  showMaxRows = false;
  showMaxLevels = false;
  showDetailList = false;
  showSelectedRecord = false;
  showAdvancedTab = false;
  showMultiThreadInputs = false;
  multiThreadFieldNamesLoaded = false;
  multiThreadFieldNames: string[] = [];
  multiThreadFieldNameOptions: string[] = [];
  readonly defaultThreadNumber = 2;

  ngOnInit() {
    this.useCompactFormLayout = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;
    // Setup form with default values
    this.printForm = this.formBuilder.group({
      csvFormat: [ 1, Validators.required ],
      columnHeaderType: [ 0, Validators.required ],
      name: [ '', Validators.required ],
      maxRows: [ 5000, Validators.required ],
      recordLimitOption: [ 'default', Validators.required ],
      allRecords: [ false, Validators.required ],
      exportOption: [ { value: 0, disabled: true }, Validators.required ],
      detailOption: [ { value: 0, disabled: true }, Validators.required ],
      maxLevels: [ { value: 3, disabled: true }, Validators.required ],
      detailList: [ '', Validators.required ],
      selectedRecordOnly: [ { value: false, disabled: true }, Validators.required ],
      needsMultiThreading: [ false ],
      multiThreadFieldName: [ '', Validators.required ],
      numberOfThreads: [ 1, Validators.required ],
    });
    const controls = this.printForm.controls;
    controls.name.setValue(this.printInfo.name);

    if (!this.printInfo.includeRecordLimit && !this.printInfo.isTreeList && !this.isUseAllActorGroups) {
      // Matrix form does not use tabs
      // List component and report catalog export to csv will display tabs
      this.useTabs = false;
    }

    if (this.lmPrintRequestService.getConfigFeatureForMultiThreading()) {
      this.showAdvancedTab = true;
    }

    if (!this.printInfo.includeRecordLimit) {
      controls.allRecords.disable();
      controls.recordLimitOption.disable();
      controls.maxRows.disable();
    }

    this.csvType = this.printInfo.csvType;
    this.includeExportOption = (
        this.csvType === AppConstants.LIST_WITH_DETAILS_CSV ||
        this.csvType === AppConstants.TREE_LIST_CSV ||
        this.csvType === AppConstants.TREE_LIST_WITH_DETAILS_CSV
    );

    if (this.includeExportOption) {
      this.exportOptions = this.getExportOptions();
      controls.exportOption.enable();
      controls.exportOption.setValue(this.exportOptions[0].value);
    }

    if (this.printInfo.detailList && this.printInfo.detailList.length > 0) {
      this.detailList = this.printInfo.detailList;
    }

    this.reportDistGroupLabel = Soho.Locale.translate('PDFRepDistGroup');
    this.sourceFieldName = 'ReportDistributionGroup';
  }

  ngAfterViewInit() {
    const controls = this.printForm.controls;
    let subscription: Subscription;
     // exportOption value determines if maxLevels field enable/disable
    subscription = controls.exportOption.valueChanges.subscribe((value) => {
      const maxLevelsControl = controls.maxLevels;

      let disable = true;
      if (Number(value) === AppConstants.CSV_SPECIFIED_ACTION || Number(value) === AppConstants.CSV_SPECIFIED_WITH_DETAILS_ACTION) {
        disable = false;
      }

      this.showMaxLevels = !disable;
      if (disable) {
        maxLevelsControl.disable();
      } else {
        maxLevelsControl.enable();
      }
    });
    this.subscriptions.push(subscription);

    subscription = controls.recordLimitOption.valueChanges.subscribe((value) => {
      const maxRowsControl = this.printForm.get('maxRows');
      const allRecordsControl = this.printForm.get('allRecords');
      const csvFormat = this.printForm.get('csvFormat');

      allRecordsControl.setValue(value === 'all');
      this.showMaxRows = value === 'custom';
      this.allRecordsAlert?.removeAllMessages();

      switch (value) {
        case 'all':
          maxRowsControl.disable();
          this.checkExcelRecordLimit();
          break;
        case 'default':
          maxRowsControl.enable();
          maxRowsControl.setValue(5000);
          break;
        case 'large':
          maxRowsControl.enable();
          maxRowsControl.setValue(250000);
          break;
        case 'small':
          maxRowsControl.enable();
          maxRowsControl.setValue(100);
          break;
        case 'custom':
          maxRowsControl.enable();
          maxRowsControl.setValue(5000);
          // enforce max limit of the spinbox
          if (parseInt(csvFormat?.value) === 2) {
            this.excelRowLimit = EXCEL_ROW_LIMIT;
          }
          this.checkExcelRecordLimit();
      }
    });
    this.subscriptions.push(subscription);

    subscription = controls.needsMultiThreading.valueChanges.subscribe((value) => {
      this.showMultiThreadInputs = value as boolean;
      if (!this.showMultiThreadInputs) {
        // Reset the values back to default
        this.printForm.controls.numberOfThreads.setValue(1);
        this.printForm.controls.multiThreadFieldName.setValue('');
      } else {
        this.printForm.controls.numberOfThreads.setValue(this.defaultThreadNumber);
        this.loadMultiThreadFields();
      }
    });
    this.subscriptions.push(subscription);

    if (this.lmLookupBasicCsv) {
      // Set the select list url for ReportDistributionGroup
      this.lmLookupBasicCsv.viewName = 'CSVOptions'; // Dummy data for handcoded forms. (There is no formSpec or fromDataView)
      this.lmLookupBasicCsv.selectListUrl = this.lmPrintRequestService.getReportDistributionSelectUrl();
    }

    this.allRecordsAlert = this.alerts.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === 'recordLimitAlert');
    this.dynamicActorsAlert = this.alerts.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === 'dynamicActors');

    if (this.printInfo.includeRecordLimit) {
      const exportDefaultRecordLimit = this.lmGlobalPersonalizationService.getGlobalPersonalization().exportDefaultRecordLimit;
      if (exportDefaultRecordLimit === 'no-limit') {
        controls.recordLimitOption.setValue('all');
        controls.maxRows.disable();
        const alertMessage =  Soho.Locale.translate('PDFIncludingAllRecords');
        this.allRecordsAlert?.addInlineMessage(alertMessage, 'info', true);
      }
    }

    subscription = controls.maxRows.valueChanges.subscribe(() => {
      this.checkExcelRecordLimit();
    });
    this.subscriptions.push(subscription);

    subscription = controls.csvFormat.valueChanges.subscribe((value) => {
      const recordLimitOption = this.printForm.get('recordLimitOption');
      if (parseInt(value) === 1) {
        this.excelRowLimit = undefined;
      } else if (parseInt(value) === 2) {
        if (recordLimitOption?.value == 'custom') {
          this.excelRowLimit = EXCEL_ROW_LIMIT;
        }
      }
      this.checkExcelRecordLimit();
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private checkExcelRecordLimit(): void {
    const csvFormat = this.printForm.get('csvFormat');
    const maxRows = this.printForm.get('maxRows');
    const recordLimitOption = this.printForm.get('recordLimitOption');

    let alertMessage = '';
    
    if (parseInt(csvFormat?.value) === 1) {
      this.allRecordsAlert?.removeAllMessages();
      if (recordLimitOption?.value == 'all') {
        alertMessage = Soho.Locale.translate('PDFIncludingAllRecords');
        this.allRecordsAlert?.addInlineMessage(alertMessage, 'info', true);   
      } 
    } else if (parseInt(csvFormat?.value) === 2) {
      if ((recordLimitOption?.value == 'custom' && parseInt(maxRows?.value) > this.excelRowLimit) || recordLimitOption?.value == 'all') {  
        this.allRecordsAlert?.removeAllMessages();
        alertMessage = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('ExcelRecordLimit'), [ EXCEL_ROW_LIMIT ]);
        this.allRecordsAlert?.addInlineMessage(alertMessage, 'info', true);
      }
    } 
  }

  get resource(): Resource {
    const selectedRecordOnly = !!this.printForm.controls.selectedRecordOnly.value;
    if (selectedRecordOnly
        && this.printInfo.dataViewSet.data.length
        && this.printInfo.selectedRows.length) {
      const listResource = <ListResource>this.printInfo.resource;
      return new ListResource({
        resourceId: this.printInfo.dataViewSet.data[this.printInfo.selectedRows[0]].resourceId,
        listName: listResource.listName
      });
    }

    return this.printInfo.resource;
  }

  get isUseAllActorGroups(): boolean {
    return this.lmPrintRequestService.getConfigFeatureUseAllActorGroups();
  }

  public checkIfFormIsValid(): boolean {
    let isValid = true;
    if (this.atLeastOneDynamicActor) {
      isValid = this.dynamicActorsComponent.values.length > 0;
      if (!isValid) {
        // this.dynamicActorsComponent.dropdown.setFocus();
        const options: SohoMessageOptions = {
          title: Soho.Locale.translate('ValidationError'),
          message: Soho.Locale.translate('PDFAtLeastOneDynamicActor'),
          showCloseBtn: true };

        this.sohoMessageService
          .alert(options)
          .open();
      }
    }
    return isValid;
  }

  get requestData(): LMPrintRequestData {
    // Default scheduleView derived from landmark.popupmenu.js
    let value = this.printForm.controls.name.value;
    value = `${value}`;

    const scheduleView = { fields: { Name: { value } } };
    const requestData = <LMPrintRequestData>{
      requestType: 'scheduleCsv',
      scheduleView
    };

    if (this.printInfo.dataView) {
      requestData.dataView = this.printInfo.dataView;
    }

    if (this.printInfo.selectRequestData) {
      requestData.selectRequestData = this.printInfo.selectRequestData;
    }

    if (this.showDynamicActors && this.dynamicActorsComponent) {
      requestData.dynamicActors = this.dynamicActorsComponent.values;
    }

    if (this.printInfo.chartNames) {
      requestData.chartNames = this.printInfo.chartNames;
    }
    return requestData;
  }

  /**
   * Returns configured form values
   * @return
   */
  get requestParams(): LMCsvPrintParams {
    const commonParams = this.lmPrintRequestService.getCommonRequestParams(this.printInfo);

    const params =  <LMCsvPrintParams>{
      output: 'csv',
      ...this.printForm.value,
      ...commonParams
    };

    if (!params.needsMultiThreading) {
      params.numberOfThreads = 1;
    }

    // Lookup component does not implement ControlAccessorValue. Landmark forms does not use formgroup / formcontrol to manage model data.
    if (this.lmLookupBasicCsv && this.lmLookupBasicCsv.lookupValue) {
      params.reportDistGroup = this.lmLookupBasicCsv.lookupValue;
    }

    if (this.printInfo.selectedDetailPanelIndex > -1) {
      params.selectedDetailPanelIndex = this.printInfo.selectedDetailPanelIndex;
    }

    if (this.printInfo.isTreeList && !params.hasOwnProperty('maxLevels')) {
      params.maxLevels = -1;
    }

    if (!params['pagepanel'] && (this.printInfo.detailList?.length > 0 && !this.printInfo.hasDetailSections)) {
      params['pagepanel'] = this.printInfo.detailList[0].parentPanel;
    }

    return params;
  }

  /**
   * Returns Toast Success Message
   * @param          if csv is scheduled
   * @return
   */
  getSuccessMessage(scheduled: boolean, name = ''): SohoToastOptions {
    const title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate(scheduled ? 'CSVIsScheduled' : 'GeneratingCSV'), name);
    const message = Soho.Locale.translate('PleaseVisitMyPrintFiles');

    return { title, message };
  }

  /**
   * Returns Toast Error Message
   * @param          if csv is scheduled
   * @return
   */
  getErrorMessage(scheduled: boolean): SohoToastOptions {
    const title = Soho.Locale.translate(scheduled ? 'CSVScheduleError' : 'CSVGenerationError');
    const message = Soho.Locale.translate(scheduled ? 'CSVWasNotScheduled' : 'CSVWasNotGenerated');

    return { title,  message };
  }

  reportDistributionGroupChanged(event: LMFieldValueChangedEvent) {
    const key = this.lmLookupBasicCsv?.lookupValue;
    this.dynamicActorsAlert.removeAllMessages();

    if (!key) {
      this.dynamicActorsLabel = Soho.Locale.translate('PDFAdHocActors');
      this.showDynamicActors = false;
      this.atLeastOneDynamicActor = false;
      return;
    }

    const baseUrl = this.lmUrlUtilService.getBaseUrl();
    const resourceId = encodeURIComponent(`ReportDistributionGroup(${key})`);
    const url = `${baseUrl}/form/${resourceId}`;
    const fieldList = [ 'HasDynamicActorReportDistribution', 'DynamicActorsOnlyProfiles' ];

    this.lmFromRequestService.getFieldValues(url, fieldList).subscribe(response => {
      let showActors = false;
      let requiresAtLeastOneActor = false;

      if (response?.status === 'Completed') {
        const fields = response.fields;
        if (fields.hasOwnProperty('HasDynamicActorReportDistribution') && fields.hasOwnProperty('DynamicActorsOnlyProfiles')) {
          const hasDynamicActorsFld: LMField = fields.HasDynamicActorReportDistribution;
          const onlyDynamicActorsFld: LMField = fields.DynamicActorsOnlyProfiles;
          showActors = hasDynamicActorsFld.value as boolean;
          requiresAtLeastOneActor = onlyDynamicActorsFld.value as boolean;
        }
      }

      this.dynamicActorsLabel = requiresAtLeastOneActor ? Soho.Locale.translate('PDFRequiredAdHocActors') : Soho.Locale.translate('PDFAdHocActors');
      this.showDynamicActors = showActors;
      this.atLeastOneDynamicActor = requiresAtLeastOneActor;

      if (showActors) {
        const alertMessage = requiresAtLeastOneActor ? Soho.Locale.translate('PDFNeedAtLeastOneActor') : Soho.Locale.translate('PDFAddAdditionalActors');
        this.dynamicActorsAlert.addInlineMessage(alertMessage, 'info', true);
      }
    });
  }

  getExportOptions(): LMPrintOption[] {
    const options: { [index: string]: LMPrintOption[] } = {};
    const exportOption = (value, label) => ({value, label: Soho.Locale.translate(label)});

    options[AppConstants.LIST_WITH_DETAILS_CSV] = [
      exportOption(AppConstants.CSV_ACTION, 'CSVCurrentList'),
      exportOption(AppConstants.CSV_WITH_DETAILS_ACTION, 'CSVListWithDetails'),
      exportOption(AppConstants.CSV_WITH_DETAILS_ACTION_DETAILSONLY, 'CSVDetailListOnly'),
    ];

    options[AppConstants.TREE_LIST_CSV] = [
      exportOption(AppConstants.CSV_EXPANDED_ACTION, 'CSVTreeListAllLevels'),
      exportOption(AppConstants.CSV_SPECIFIED_ACTION, 'CSVTreeListMaxLevel')
    ];
    options[AppConstants.TREE_LIST_WITH_DETAILS_CSV] = [
      exportOption(AppConstants.CSV_EXPANDED_ACTION, 'CSVTreeListAllLevels'),
      exportOption(AppConstants.CSV_EXPANDED_WITH_DETAILS_ACTION, 'CSVTreeListExpandedDetails'),
      exportOption(AppConstants.CSV_SPECIFIED_ACTION, 'CSVTreeListMaxLevel'),
      exportOption(AppConstants.CSV_SPECIFIED_WITH_DETAILS_ACTION, 'CSVTreeListMaxLevelDetails'),
    ];

    return options[this.csvType];
  }

  getDetailOptions() {
    const detailOption = (value, label) => ({value, label: Soho.Locale.translate(label)});
    const controls = this.printForm.controls;
    const exportOptionVal = Number(controls.exportOption.value);
    this.detailsOptions = [];
    this.includeDetailOption = false;
    this.showDetailList = false;
    this.showSelectedRecord = false;
    controls.selectedRecordOnly.reset();

    controls.detailOption.disable();
    controls.detailList.disable();
    controls.selectedRecordOnly.disable();

    if (exportOptionVal === AppConstants.CSV_WITH_DETAILS_ACTION ||
        exportOptionVal === AppConstants.CSV_WITH_DETAILS_ACTION_DETAILSONLY ||
        exportOptionVal == AppConstants.CSV_EXPANDED_WITH_DETAILS_ACTION ||
        exportOptionVal == AppConstants.CSV_SPECIFIED_WITH_DETAILS_ACTION){

      if (this.printInfo.singleRecordSelected) {
        this.showSelectedRecord = true;
        controls.selectedRecordOnly.enable();
      }

      if (exportOptionVal !== AppConstants.CSV_WITH_DETAILS_ACTION_DETAILSONLY) {
        this.includeDetailOption = true;
        controls.detailOption.enable();
        this.detailsOptions = [
          detailOption(AppConstants.CSV_DETAILS_COMBINED, 'CSVDetailsCombined'),
          detailOption(AppConstants.CSV_DETAILS_CONTEXT, 'CSVDetailsContext')
        ];
        controls.detailOption.setValue(this.detailsOptions[0].value);
      }

      if (this.detailList && this.detailList.length > 0) {
        this.showDetailList = true;
        controls.detailList.setValue(this.detailList[0].name);

        if (this.detailList.length > 0) {
          controls.detailList.enable();
        }
      }
    }
  }

  private loadMultiThreadFields() {
    // Use printInfo dataViewSet header record to get the business class name
    // Get all the fields and push it into the dropdown
    const businessClass = this.printInfo.dataViewSet?.header.busClassName;
    const uniqueIdFld = this.printInfo.dataViewSet?.header.uniqueIdFld;
    if (businessClass) {
      this.lmPrintRequestService.getMultiThreadFieldNames(businessClass).pipe(first()).subscribe({
        next: (response: LMListPagingResponse) => {
          if (response.status === 'COMPLETED') {
            if (response.dataViewSet.data) {
              // eslint-disable-next-line @typescript-eslint/no-this-alias
              const csvDialogComp = this;
              response.dataViewSet.data.forEach((dv) => {
                csvDialogComp.multiThreadFieldNames.push(dv.fields['BusinessField'].value as string);
              });
              this.multiThreadFieldNameOptions = [ ...this.multiThreadFieldNames ];
              if (uniqueIdFld) {
                // Some business class may disable unique id. If it is available, set it as default value.
                this.printForm.controls.multiThreadFieldName.setValue(uniqueIdFld);
              }
              this.multiThreadFieldNamesLoaded = true;
              this.multiThreadAlert = this.alerts.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === 'multiThreadAlert');
              this.multiThreadAlert?.addInlineMessage(Soho.Locale.translate('MultiThreadAlert'), 'info', true);
            }
          } else {
            // No field names to select
            // TODO: how to display error
            this.printForm.controls.multiThreadFieldName.setValue(uniqueIdFld ? uniqueIdFld : '');
          }
        },
        error: (e) => {
          // No Field names to select
          // TODO: how to display error
          this.printForm.controls.numberOfThreads.setValue(this.defaultThreadNumber);
          this.printForm.controls.multiThreadFieldName.setValue(uniqueIdFld ? uniqueIdFld : '');
        }
      });
    }
  }
}
